import {AnimatePresence, motion} from 'framer-motion';
import {
  InvitedUser,
  Palette,
  sanitizeString,
  SSOLocationMetadata,
  InvitedUserFields,
  TranslatedLanguage,
} from 'hubbl-shared';
import Papa from 'papaparse';
import {memo, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import styled from 'styled-components';
import {Api} from '../../api';
import {
  AssetIconAdd,
  AssetIconTrash,
  AssetIconDownload,
  AssetIconEdit,
} from '../../assets';
import {useAppDispatch, useAppSelector, useI18n} from '../../hooks';
import {CompanySelector} from '../../store/company/CompanySelector';
import {GlobalActions} from '../../store/global/GlobalActions';
import {UserSelector} from '../../store/user/UserSelector';
import {CloseButton} from '../buttons/close-button/CloseButton';
import {IconButton} from '../buttons/icon-button/IconButton';
import {PrimaryButton} from '../buttons/primary-button/PrimaryButton';
import {SecondaryButton} from '../buttons/secondary-button/SecondaryButton';
import {Input} from '../input/Input';
import {TextHeading2} from '../text/TextHeading2';
import {TextHeading5} from '../text/TextHeading5';
import {InviteUsersStatus} from './InviteUsersStatus';
import {TextHeading7} from '../text/TextHeading7';
import {TextHeading8} from '../text/TextHeading8';
import {TextHeading6} from '../text/TextHeading6';

const EmailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled(motion.div)`
  background: ${Palette.white};
  border-radius: 16px;
  width: 100%;
  max-width: 1200px;
  max-height: 95vh;
  min-height: 600px;
  position: relative;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  padding: 16px;
`;

const ContentArea = styled.div`
  padding: 0 0px;
  overflow-y: auto;
  flex: 1;
  min-height: 400px;
  max-height: calc(95vh - 200px);
`;

const DropZone = styled.div<{isDragging: boolean}>`
  border: 2px dashed
    ${props => (props.isDragging ? Palette.primaryCherokee : '#ccc')};
  border-radius: 8px;
  padding: 32px;
  text-align: center;
  margin: 16px 16px;
  background: ${props =>
    props.isDragging ? Palette.primaryCherokee : 'transparent'};
  cursor: pointer;
  transition: all 0.2s ease-in-out;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background-color: ${Palette.primarySubtleGreen};
  position: sticky;
  top: 0;
  z-index: 1;
`;

const TableHeaderCell = styled.th`
  padding: 12px 16px;
  text-align: left;
  font-weight: 500;
  color: ${Palette.almostBlack};
  border-bottom: 1px solid ${Palette.almostBlackTransparent5};
`;

const TableBody = styled.tbody`
  tr:hover {
    transition: background-color 0.3s ease-in-out;
    background-color: ${Palette.primarySubtleGreen};
  }
`;

const TableCell = styled.td`
  padding: 12px 16px;
  border-bottom: 1px solid ${Palette.almostBlackTransparent5};
`;

const ButtonsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  padding: 16px;
  margin-top: 16px;
  background: ${Palette.white};
  border-top: 1px solid ${Palette.almostBlackTransparent5};
  position: sticky;
  bottom: 0;
  border-radius: 0 0 16px 16px;
`;

const HeadingWithButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0 16px 8px 16px;
`;

const ManualInputsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
  margin: 16px;
`;

const ManualActions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: 0 16px 16px;
`;

const Section = styled.div`
  padding: 0 16px 24px 16px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

const SectionDescription = styled.div`
  color: ${Palette.almostBlack750};
  font-size: 12px;
  line-height: 1.4;
`;

const TabBar = styled.div`
  display: flex;
  border-bottom: 2px solid ${Palette.almostBlackTransparent10};
  margin: 0 16px;
  background: ${Palette.white};
`;

const TabButton = styled.button<{isActive: boolean}>`
  flex: 1;
  padding: 16px 24px;
  background: transparent;
  border: none;
  border-bottom: 3px solid
    ${props => (props.isActive ? Palette.primaryTerraCotta : 'transparent')};
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    background: ${Palette.almostBlackTransparent5};
  }
`;

const TabContent = styled(motion.div)`
  padding: 24px 16px;
`;

const CSVUploadSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ManualEntrySection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const PendingInvitationsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const StyledTextarea = styled.textarea`
  width: 100%;
  border-radius: 8px;
  border: 1px solid ${Palette.almostBlackTransparent5};
  padding: 12px;
  font-size: 12px;
  margin-top: 8px;
  margin-bottom: 16px;
  resize: vertical;
  font-family: inherit;
  background-color: ${Palette.white};
  outline: none;
  transition: border-color 0.2s ease;
  box-sizing: border-box;
  background: ${Palette.primarySubtleGreen};
  &:focus {
    border-color: ${Palette.primaryCherokee};
    box-shadow: 0 0 0 2px ${Palette.primaryCherokee}33;
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: ${Palette.primarySubtleGreen};
  border-radius: 8px;
  margin: 0 16px 16px 16px;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: ${Palette.almostBlackTransparent5};
  }
`;

const Checkbox = styled.input`
  width: 20px;
  height: 20px;
  cursor: pointer;
  accent-color: ${Palette.primaryTerraCotta};
`;

const CheckboxLabel = styled.label`
  cursor: pointer;
  user-select: none;
  flex: 1;
`;

const EditableNameContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const EditableNameInput = styled.input<{hasWarning?: boolean}>`
  padding: 6px 10px;
  border: 1px solid
    ${props =>
      props.hasWarning
        ? Palette.systemError
        : Palette.almostBlackTransparent10};
  border-radius: 6px;
  font-size: 12px;
  font-family: inherit;
  width: 100%;
  min-width: 120px;
  background: ${props =>
    props.hasWarning ? `${Palette.systemError}10` : Palette.white};
  outline: none;
  transition: all 0.2s ease;

  &:focus {
    border-color: ${props =>
      props.hasWarning ? Palette.systemError : Palette.primaryCherokee};
    box-shadow: 0 0 0 2px
      ${props =>
        props.hasWarning
          ? `${Palette.systemError}33`
          : `${Palette.primaryCherokee}33`};
  }
`;

const WarningIcon = styled.div`
  color: ${Palette.systemError};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: help;
  font-size: 16px;
  font-weight: bold;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: ${Palette.systemError}20;
  flex-shrink: 0;
`;

const StyledSelect = styled.select`
  padding: 12px 16px;
  border: 1px solid ${Palette.almostBlackTransparent10};
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  background: ${Palette.primarySubtleGreen};
  outline: none;
  cursor: pointer;
  transition: border-color 0.2s ease;
  width: 100%;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23666' d='M6 8L1 3h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;

  &:focus {
    border-color: ${Palette.primaryCherokee};
    box-shadow: 0 0 0 2px ${Palette.primaryCherokee}33;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// Detect problematic characters that indicate encoding issues
const PROBLEMATIC_CHARS = /[\uFFFD\u00BF\u00BD\u00BC\u0080-\u009F]/;
const hasProblematicCharacters = (text: string): boolean => {
  return PROBLEMATIC_CHARS.test(text);
};

interface InviteUsersModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyId: string;
}

type ManualInviteForm = {
  email: string;
  fullName: string;
  department: string;
  jobTitle: string;
  ssoOfficeLocation?: string;
  ssoCompanyName?: string;
  ssoDepartmentId?: string;
};

const DEFAULT_MANUAL_INVITE: ManualInviteForm = {
  email: '',
  fullName: '',
  department: '',
  jobTitle: '',
  ssoOfficeLocation: '',
  ssoCompanyName: '',
  ssoDepartmentId: '',
};

// Column aliases for flexible CSV parsing (case-insensitive)
const CSV_COLUMN_ALIASES: Record<string, string[]> = {
  email: ['email', 'e-mail', 'mail', 'Email'],
  fullname: [
    'fullname',
    'full_name',
    'name',
    'navn',
    'displayname',
    'display_name',
    'display name',
  ],
  firstname: ['firstname', 'first_name', 'fornavn'],
  lastname: ['lastname', 'last_name', 'efternavn'],
  department: ['department', 'afdeling', 'dept', 'team name'],
  jobtitle: [
    'jobtitle',
    'job title',
    'job_title',
    'title',
    'ekstern titel',
    'stilling',
  ],
  ssocompanyname: [
    'companyname',
    'company_name',
    'company',
    'arbejdsgiver',
    'employer',
    'ssocompanyname',
    'working for legal unit',
  ],
  ssoofficelocation: [
    'officelocation',
    'office_location',
    'location',
    'office',
    'andet p-nummer',
    'p-nummer',
    'kontor',
    'ssoofficelocation',
    'site',
  ],
};

/**
 * Finds a matching column name from the CSV headers using aliases
 */
const findColumnByAlias = (
  headers: string[],
  fieldName: string,
): string | null => {
  const normalizedHeaders = headers.map(h => h.toLowerCase().trim());
  const aliases = CSV_COLUMN_ALIASES[fieldName.toLowerCase()] || [
    fieldName.toLowerCase(),
  ];

  for (const alias of aliases) {
    const index = normalizedHeaders.indexOf(alias.toLowerCase());
    if (index !== -1) {
      return headers[index];
    }
  }
  return null;
};

const REQUIRED_CSV_HEADERS = ['email', 'fullname', 'department'];

/**
 * Extract location options from organizational metadata
 */
const getLocationOptions = (
  organizationalMetadata?: Record<string, SSOLocationMetadata>,
): Array<{id: string; label: string}> => {
  if (!organizationalMetadata) return [];
  return Object.entries(organizationalMetadata).map(([id, data]) => ({
    id,
    label: data.label,
  }));
};

/**
 * Extract subCompany options for a specific location
 */
const getSubCompanyOptionsForLocation = (
  organizationalMetadata: Record<string, SSOLocationMetadata> | undefined,
  locationId: string,
): Array<{id: string; label: string}> => {
  if (!organizationalMetadata || !locationId) return [];
  const location = organizationalMetadata[locationId];
  if (!location) return [];
  return Object.entries(location.subCompanies).map(([id, data]) => ({
    id,
    label: data.label,
  }));
};

/**
 * Extract department options for a specific location and company
 */
const getDepartmentOptionsForCompany = (
  organizationalMetadata: Record<string, SSOLocationMetadata> | undefined,
  locationId: string,
  companyId: string,
): Array<{id: string; label: string}> => {
  if (!organizationalMetadata || !locationId || !companyId) return [];
  const location = organizationalMetadata[locationId];
  if (!location) return [];
  const company = location.subCompanies[companyId];
  if (!company) return [];
  return Object.entries(company.departments).map(([id, data]) => ({
    id,
    label: data.label,
  }));
};

const buildDepartmentField = (label: string) => {
  const trimmedLabel = label.trim();
  return {
    id: sanitizeString(trimmedLabel),
    label: {
      [TranslatedLanguage.English]: trimmedLabel,
      [TranslatedLanguage.Danish]: trimmedLabel,
    },
  };
};

const buildInviteFromManualForm = (
  form: ManualInviteForm,
  companyId: string,
  companyName: string,
): InvitedUserFields => {
  const invite: InvitedUserFields = {
    email: form.email.trim().toLowerCase(),
    fullName: form.fullName.trim(),
    jobTitle: form.jobTitle.trim(),
    companyId,
    companyName,
    department: buildDepartmentField(form.department),
  };

  // Add optional SSO fields if provided
  if (form.ssoCompanyName?.trim()) {
    invite.ssoCompanyName = form.ssoCompanyName.trim();
  }
  if (form.ssoOfficeLocation?.trim()) {
    invite.ssoOfficeLocation = form.ssoOfficeLocation.trim();
  }

  return invite;
};

const normalizeCsvValue = (value?: string | null) =>
  (value ?? '').toString().trim();

type TabType = 'csv' | 'manual' | 'pending';

export const InviteUsersModal = memo(
  ({isOpen, onClose, companyId}: InviteUsersModalProps) => {
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [pendingInvites, setPendingInvites] = useState<InvitedUserFields[]>(
      [],
    );
    const [isDragging, setIsDragging] = useState(false);
    const [manualInvite, setManualInvite] = useState<ManualInviteForm>(
      DEFAULT_MANUAL_INVITE,
    );
    const [invitedUsersList, setInvitedUsersList] = useState<InvitedUser[]>([]);
    const [activeTab, setActiveTab] = useState<TabType>('csv');
    const prevTabRef = useRef<TabType>('csv');
    const fileInputRef = useRef<HTMLInputElement>(null);
    const tableContainerRef = useRef<HTMLDivElement>(null);
    const company = useAppSelector(CompanySelector.getCompanyDetails);
    const userDetails = useAppSelector(UserSelector.getUserDetails);
    const t = useI18n();

    const defaultDescription = useMemo(() => {
      return t.invite.inviteMessage.replace(
        '{companyName}',
        company?.companyName || '',
      );
    }, [company, t]);
    const [customDescription, setCustomDescription] =
      useState<string>(defaultDescription);
    const [sendInviteEmails, setSendInviteEmails] = useState<boolean>(true);

    const pendingInvitationsCount = useMemo(() => {
      return invitedUsersList.filter(user => user.status === 'pending').length;
    }, [invitedUsersList]);

    const hasEncodingIssues = useMemo(() => {
      return pendingInvites.some(invite =>
        hasProblematicCharacters(invite.fullName),
      );
    }, [pendingInvites]);

    // Check if company has SSO enabled (has organizational metadata)
    const hasSSOEnabled = useMemo(() => {
      return (
        company?.organizationalMetadata &&
        Object.keys(company.organizationalMetadata).length > 0
      );
    }, [company]);

    // Get dropdown options for SSO fields
    const locationOptions = useMemo(
      () => getLocationOptions(company?.organizationalMetadata),
      [company?.organizationalMetadata],
    );

    const subCompanyOptions = useMemo(
      () =>
        getSubCompanyOptionsForLocation(
          company?.organizationalMetadata,
          manualInvite.ssoOfficeLocation || '',
        ),
      [company?.organizationalMetadata, manualInvite.ssoOfficeLocation],
    );

    const departmentOptions = useMemo(
      () =>
        getDepartmentOptionsForCompany(
          company?.organizationalMetadata,
          manualInvite.ssoOfficeLocation || '',
          manualInvite.ssoCompanyName || '',
        ),
      [
        company?.organizationalMetadata,
        manualInvite.ssoOfficeLocation,
        manualInvite.ssoCompanyName,
      ],
    );

    // Auto-select if only one option exists
    useEffect(() => {
      if (!hasSSOEnabled) return;

      // Auto-select single location
      if (locationOptions.length === 1 && !manualInvite.ssoOfficeLocation) {
        setManualInvite(prev => ({
          ...prev,
          ssoOfficeLocation: locationOptions[0].id,
        }));
      }
    }, [hasSSOEnabled, locationOptions, manualInvite.ssoOfficeLocation]);

    useEffect(() => {
      if (!hasSSOEnabled || !manualInvite.ssoOfficeLocation) return;

      // Auto-select single company
      if (subCompanyOptions.length === 1 && !manualInvite.ssoCompanyName) {
        setManualInvite(prev => ({
          ...prev,
          ssoCompanyName: subCompanyOptions[0].id,
        }));
      }
    }, [
      hasSSOEnabled,
      subCompanyOptions,
      manualInvite.ssoOfficeLocation,
      manualInvite.ssoCompanyName,
    ]);

    useEffect(() => {
      if (
        !hasSSOEnabled ||
        !manualInvite.ssoOfficeLocation ||
        !manualInvite.ssoCompanyName
      )
        return;

      // Auto-select single department
      if (departmentOptions.length === 1 && !manualInvite.ssoDepartmentId) {
        setManualInvite(prev => ({
          ...prev,
          ssoDepartmentId: departmentOptions[0].id,
          department: departmentOptions[0].label,
        }));
      }
    }, [
      hasSSOEnabled,
      departmentOptions,
      manualInvite.ssoOfficeLocation,
      manualInvite.ssoCompanyName,
      manualInvite.ssoDepartmentId,
    ]);

    const getTabOrder = (tab: TabType): number => {
      const order: Record<TabType, number> = {
        csv: 0,
        manual: 1,
        pending: 2,
      };
      return order[tab];
    };

    const getAnimationDirection = (
      currentTab: TabType,
      previousTab: TabType,
    ): {initialX: number; exitX: number} => {
      const currentOrder = getTabOrder(currentTab);
      const prevOrder = getTabOrder(previousTab);
      const isMovingRight = currentOrder > prevOrder;

      if (isMovingRight) {
        // Moving right: slide in from right, exit to left
        return {initialX: 30, exitX: -30};
      } else {
        // Moving left: slide in from left, exit to right
        return {initialX: -30, exitX: 30};
      }
    };

    const handleTabChange = (newTab: TabType) => {
      if (newTab !== activeTab) {
        prevTabRef.current = activeTab;
        setActiveTab(newTab);
      }
    };

    const fetchInvitedUsers = useCallback(async () => {
      const users = await Api.invitedUsers.getInvitedUsers(companyId);
      setInvitedUsersList(users);
    }, [companyId]);

    useEffect(() => {
      if (isOpen && companyId) {
        fetchInvitedUsers();
      }
    }, [fetchInvitedUsers, isOpen, companyId]);

    useEffect(() => {
      setError(null);
      setPendingInvites([]);
      setLoading(false);
      setIsDragging(false);
      setManualInvite(DEFAULT_MANUAL_INVITE);
      setCustomDescription(defaultDescription);
      setSendInviteEmails(true);
      setActiveTab('csv');
      prevTabRef.current = 'csv';
    }, [isOpen, defaultDescription]);

    const validateEmailDomain = (email: string): boolean => {
      if (!company?.settings?.restrictedMailDomains?.length) return true;

      const domain = email.split('@')[1];
      return company.settings.restrictedMailDomains.includes(domain);
    };

    const manualFormIsComplete = useMemo(() => {
      const baseComplete =
        manualInvite.email.trim().length > 0 &&
        manualInvite.fullName.trim().length > 0 &&
        manualInvite.jobTitle.trim().length > 0 &&
        manualInvite.department.trim().length > 0;

      if (hasSSOEnabled) {
        return (
          baseComplete &&
          !!manualInvite.ssoOfficeLocation &&
          !!manualInvite.ssoCompanyName &&
          !!manualInvite.ssoDepartmentId
        );
      }

      return baseComplete;
    }, [manualInvite, hasSSOEnabled]);

    const handleManualFieldChange =
      (field: keyof ManualInviteForm) =>
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setManualInvite(prev => ({
          ...prev,
          [field]: value,
        }));
      };

    const handleAddManualInvite = () => {
      if (!manualFormIsComplete) {
        setError(t.common.pleaseFillRequiredFields);
        return;
      }

      const normalizedEmail = manualInvite.email.trim().toLowerCase();

      if (!EmailRegex.test(normalizedEmail)) {
        setError(t.common.pleaseEnterValidEmailAddress);
        return;
      }

      if (!validateEmailDomain(normalizedEmail)) {
        setError(
          `${t.common.emailDomainNotAllowed} ${company?.settings?.restrictedMailDomains?.join(
            ', ',
          )}`,
        );
        return;
      }

      if (pendingInvites.some(invite => invite.email === normalizedEmail)) {
        setError(t.common.thisEmailAlreadyInList);
        return;
      }

      const companyName = company?.companyName ?? '';

      // For SSO, resolve IDs to labels
      let ssoCompanyLabel: string | undefined;
      let ssoLocationLabel: string | undefined;
      if (
        hasSSOEnabled &&
        manualInvite.ssoOfficeLocation &&
        manualInvite.ssoCompanyName
      ) {
        const locationData =
          company?.organizationalMetadata?.[manualInvite.ssoOfficeLocation];
        ssoLocationLabel = locationData?.label;
        ssoCompanyLabel =
          locationData?.subCompanies[manualInvite.ssoCompanyName]?.label;
      }

      const newInvite = buildInviteFromManualForm(
        {
          email: manualInvite.email,
          fullName: manualInvite.fullName,
          department: manualInvite.department,
          jobTitle: manualInvite.jobTitle,
          ssoCompanyName: ssoCompanyLabel,
          ssoOfficeLocation: ssoLocationLabel,
        },
        companyId,
        companyName,
      );

      setPendingInvites([...pendingInvites, newInvite]);
      setManualInvite(DEFAULT_MANUAL_INVITE);
      setError(null);
    };

    const handleDragOver = (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(true);
    };

    const handleDragLeave = () => {
      setIsDragging(false);
    };

    const parseCsvFile = async (file: File): Promise<ManualInviteForm[]> => {
      // Read file as ArrayBuffer
      const arrayBuffer = await file.arrayBuffer();
      const bytes = new Uint8Array(arrayBuffer);

      let fileContent: string;

      // Check for UTF-8 BOM
      const hasUtf8Bom =
        bytes.length >= 3 &&
        bytes[0] === 0xef &&
        bytes[1] === 0xbb &&
        bytes[2] === 0xbf;

      if (hasUtf8Bom) {
        // File has UTF-8 BOM, definitely UTF-8
        const decoder = new TextDecoder('utf-8');
        fileContent = decoder.decode(arrayBuffer);
        if (fileContent.charCodeAt(0) === 0xfeff) {
          fileContent = fileContent.slice(1);
        }
      } else {
        // No BOM - try to determine encoding
        // First, check if file is valid UTF-8 by trying strict decoding
        let isValidUtf8 = true;
        try {
          const utf8Decoder = new TextDecoder('utf-8', {fatal: true});
          fileContent = utf8Decoder.decode(arrayBuffer);
        } catch {
          isValidUtf8 = false;
          fileContent = '';
        }

        // If not valid UTF-8, or if the result contains replacement characters, use Windows-1252
        if (!isValidUtf8 || fileContent.includes('\uFFFD')) {
          const win1252Decoder = new TextDecoder('windows-1252');
          fileContent = win1252Decoder.decode(arrayBuffer);
        }
      }

      // Auto-detect delimiter (semicolon for European Excel or comma)
      const firstLine = fileContent.split('\n')[0] || '';
      const delimiter = firstLine.includes(';') ? ';' : ',';

      const withHeader = Papa.parse<Record<string, string>>(fileContent, {
        header: true,
        skipEmptyLines: true,
        delimiter,
        transformHeader: header => header.trim(),
      });

      const headerFields = withHeader.meta.fields ?? [];

      // Find column mappings using aliases
      const emailCol = findColumnByAlias(headerFields, 'email');
      const fullNameCol = findColumnByAlias(headerFields, 'fullname');
      const firstNameCol = findColumnByAlias(headerFields, 'firstname');
      const lastNameCol = findColumnByAlias(headerFields, 'lastname');
      const departmentCol = findColumnByAlias(headerFields, 'department');
      const jobTitleCol = findColumnByAlias(headerFields, 'jobtitle');
      const ssoCompanyNameCol = findColumnByAlias(
        headerFields,
        'ssocompanyname',
      );
      const ssoOfficeLocationCol = findColumnByAlias(
        headerFields,
        'ssoofficelocation',
      );

      // Check if we have required columns (email + name + department)
      const hasEmail = emailCol !== null;
      const hasName = fullNameCol !== null || (firstNameCol && lastNameCol);
      const hasDepartment = departmentCol !== null;

      if (hasEmail && hasName && hasDepartment) {
        return withHeader.data.map(row => {
          // Build full name from firstname + lastname if fullname not present
          let fullName = '';
          if (fullNameCol) {
            fullName = normalizeCsvValue(row[fullNameCol]);
          } else if (firstNameCol && lastNameCol) {
            const firstName = normalizeCsvValue(row[firstNameCol]);
            const lastName = normalizeCsvValue(row[lastNameCol]);
            fullName = `${firstName} ${lastName}`.trim();
          }

          return {
            email: emailCol ? normalizeCsvValue(row[emailCol]) : '',
            fullName,
            department: departmentCol
              ? normalizeCsvValue(row[departmentCol])
              : '',
            jobTitle: jobTitleCol ? normalizeCsvValue(row[jobTitleCol]) : '',
            ssoCompanyName: ssoCompanyNameCol
              ? normalizeCsvValue(row[ssoCompanyNameCol])
              : undefined,
            ssoOfficeLocation: ssoOfficeLocationCol
              ? normalizeCsvValue(row[ssoOfficeLocationCol])
              : undefined,
          };
        });
      }

      // Fallback to positional parsing (no headers or unrecognized headers)
      const withoutHeader = Papa.parse<string[]>(fileContent, {
        header: false,
        skipEmptyLines: true,
        delimiter,
      });

      return withoutHeader.data.map(columns => ({
        email: normalizeCsvValue(columns[0]),
        fullName: normalizeCsvValue(columns[1]),
        department: normalizeCsvValue(columns[2]),
        jobTitle: normalizeCsvValue(columns[3]),
        ssoCompanyName: columns[4] ? normalizeCsvValue(columns[4]) : undefined,
        ssoOfficeLocation: columns[5]
          ? normalizeCsvValue(columns[5])
          : undefined,
      }));
    };

    const handleCsvUpload = async (file: File) => {
      setLoading(true);
      setError(null);

      try {
        const rows = await parseCsvFile(file);

        if (!rows.length) {
          setError(t.common.noValidEmailsFoundInCSV);
          return;
        }

        const companyName = company?.companyName ?? '';
        const freshInvites: InvitedUserFields[] = [];
        const skippedRows: number[] = [];
        const seenEmails = new Set(
          pendingInvites.map(invite => invite.email.toLowerCase()),
        );

        rows.forEach((row, index) => {
          const normalizedEmail = row.email.toLowerCase();
          const hasRequiredFields = row.email && row.fullName && row.department;

          if (
            !hasRequiredFields ||
            !EmailRegex.test(normalizedEmail) ||
            !validateEmailDomain(normalizedEmail) ||
            seenEmails.has(normalizedEmail)
          ) {
            skippedRows.push(index + 1);
            return;
          }

          seenEmails.add(normalizedEmail);
          freshInvites.push(
            buildInviteFromManualForm(row, companyId, companyName),
          );
        });

        if (!freshInvites.length) {
          setError(t.common.noValidEmailsFoundInCSV);
          return;
        }

        setPendingInvites(prev => [...prev, ...freshInvites]);

        if (skippedRows.length) {
          setError(
            `${skippedRows.length} row${
              skippedRows.length === 1 ? '' : 's'
            } skipped due to invalid data or duplicate emails.`,
          );
        } else {
          setError(null);
        }

        // Scroll to table after CSV is parsed and users are added
        setTimeout(() => {
          tableContainerRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }, 100);
      } catch (err) {
        setError(t.common.errorParsingCSVFile);
      } finally {
        setLoading(false);
      }
    };

    const handleDrop = (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files[0];
      if (!file) return;

      const extension = file.name.split('.').pop()?.toLowerCase() || '';
      if (extension !== 'csv') {
        setError(
          `File type ".${extension}" is not supported. Please upload a CSV file.`,
        );
        return;
      }

      handleCsvUpload(file);
    };

    const handleFileInput = async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      await handleCsvUpload(file);
      e.target.value = '';
    };

    const dispatch = useAppDispatch();

    const handleInvite = async () => {
      if (pendingInvites.length === 0 || !userDetails) return;

      if (hasEncodingIssues) {
        setError(
          'Please fix all names with encoding issues (marked with !) before inviting users.',
        );
        return;
      }

      try {
        dispatch(GlobalActions.setIsBusy(true));
        setLoading(true);
        await Api.invitedUsers.onInviteUsersByEmail({
          companyId,
          invitedUsers: pendingInvites,
          customDescription,
          sendInviteEmails,
        });

        // Clear the current list of emails to invite
        setPendingInvites([]);
        fetchInvitedUsers();
        handleTabChange('pending');
      } catch (err: any) {
        setError(err.message || t.common.failedToInviteUsers);
      } finally {
        dispatch(GlobalActions.setIsBusy(false));
        setLoading(false);
      }
    };

    const handleDeleteInvite = (emailToDelete: string) => {
      setPendingInvites(prev =>
        prev.filter(invite => invite.email !== emailToDelete),
      );
    };

    const handleUpdateInviteName = (email: string, newName: string) => {
      setPendingInvites(prev =>
        prev.map(invite =>
          invite.email === email ? {...invite, fullName: newName} : invite,
        ),
      );
    };

    const handleDownloadExample = () => {
      // Get the domain to use for example emails
      const domain =
        company?.settings?.restrictedMailDomains?.[0] || 'company.com';

      // Include Danish characters in examples to show proper encoding
      const rows = [
        ['email', 'fullName', 'department', 'jobTitle'],
        [`john.doe@${domain}`, 'John Døe', 'Engineering', 'Software Engineer'],
        [
          `jane.smith@${domain}`,
          'Jane Søndergård',
          'People Operations',
          'HR Business Partner',
        ],
        [`bob.wilson@${domain}`, 'Bob Nilsén', 'Design', 'Product Designer'],
        [
          `sarah.jones@${domain}`,
          'Sarah Åberg',
          'Finance',
          'Financial Controller',
        ],
        [`mike.brown@${domain}`, 'Mike Müller', 'Sales', 'Account Executive'],
      ];

      const csvContent = rows.map(row => row.join(',')).join('\n');

      // Add UTF-8 BOM (Byte Order Mark) so Excel recognizes the encoding correctly
      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvContent], {
        type: 'text/csv;charset=utf-8',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'example_emails.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    };

    if (!isOpen) return null;

    return (
      <AnimatePresence>
        <ModalOverlay
          onClick={onClose}
          initial={{opacity: 0}}
          animate={{opacity: 1}}
          exit={{opacity: 0}}
          transition={{duration: 0.2, ease: 'easeInOut'}}>
          <ModalContent
            initial={{y: 20, opacity: 0}}
            animate={{y: 0, opacity: 1}}
            exit={{y: -20, opacity: 0}}
            transition={{duration: 0.2, ease: 'easeInOut'}}
            onClick={e => e.stopPropagation()}>
            <CloseButton onClick={onClose} />
            <Header>
              <TextHeading2 text={t.common.inviteEmployees} />
            </Header>

            <ContentArea>
              <Section>
                <SectionHeader>
                  <TextHeading5 text={t.common.inviteMessage} />
                  <AssetIconEdit size={18} />
                </SectionHeader>
                <StyledTextarea
                  value={customDescription}
                  onChange={e => setCustomDescription(e.target.value)}
                  rows={2}
                  placeholder={t.common.enterCustomInviteMessage}
                />
              </Section>

              <CheckboxContainer
                onClick={() => setSendInviteEmails(!sendInviteEmails)}>
                <Checkbox
                  type="checkbox"
                  checked={sendInviteEmails}
                  onChange={e => setSendInviteEmails(e.target.checked)}
                  onClick={e => e.stopPropagation()}
                />
                <CheckboxLabel>
                  <TextHeading6 text="Send invitation emails to all users" />
                  <TextHeading8
                    text="Uncheck to add users without sending invitation emails. They can still sign up using their email."
                    color={Palette.almostBlack500}
                  />
                </CheckboxLabel>
              </CheckboxContainer>

              <TabBar>
                <TabButton
                  isActive={activeTab === 'csv'}
                  onClick={() => handleTabChange('csv')}>
                  <TextHeading7
                    text="CSV Upload"
                    color={
                      activeTab === 'csv'
                        ? Palette.primaryTerraCotta
                        : Palette.almostBlack500
                    }
                  />
                </TabButton>
                <TabButton
                  isActive={activeTab === 'manual'}
                  onClick={() => handleTabChange('manual')}>
                  <TextHeading7
                    text="Manual Entry"
                    color={
                      activeTab === 'manual'
                        ? Palette.primaryTerraCotta
                        : Palette.almostBlack500
                    }
                  />
                </TabButton>
                <TabButton
                  isActive={activeTab === 'pending'}
                  onClick={() => handleTabChange('pending')}>
                  <TextHeading7
                    text={`${t.common.pendingInvitations} (${pendingInvitationsCount})`}
                    color={
                      activeTab === 'pending'
                        ? Palette.primaryTerraCotta
                        : Palette.almostBlack500
                    }
                  />
                </TabButton>
              </TabBar>

              <AnimatePresence mode="wait">
                {activeTab === 'csv' &&
                  (() => {
                    const {initialX, exitX} = getAnimationDirection(
                      'csv',
                      prevTabRef.current,
                    );
                    return (
                      <TabContent
                        key="csv"
                        initial={{opacity: 0, x: initialX}}
                        animate={{opacity: 1, x: 0}}
                        exit={{opacity: 0, x: exitX}}
                        transition={{duration: 0.25, ease: 'easeInOut'}}>
                        <CSVUploadSection>
                          <HeadingWithButton>
                            <TextHeading5
                              text={t.common.addEmailsWithCSVOrManually}
                            />
                            <IconButton
                              backgroundColor={Palette.primarySubtleGreen}
                              text={t.common.downloadExampleCSV}
                              onClick={handleDownloadExample}
                              icon={<AssetIconDownload />}
                            />
                          </HeadingWithButton>
                          <SectionDescription>
                            <TextHeading7 text="Upload a CSV with email, full name, and department columns. Job title is optional." />
                          </SectionDescription>
                          <DropZone
                            isDragging={isDragging}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            onClick={() => fileInputRef.current?.click()}>
                            <input
                              ref={fileInputRef}
                              type="file"
                              accept=".csv"
                              onChange={handleFileInput}
                              style={{display: 'none'}}
                            />
                            {loading ? (
                              <TextHeading7 text={t.common.processingCSVFile} />
                            ) : (
                              <>
                                <TextHeading7
                                  text={t.common.dragAndDropCSVFileHere}
                                />
                                <TextHeading7
                                  text="Required columns: email, full name, department. Job title is optional."
                                  color={Palette.almostBlack750}
                                />
                              </>
                            )}
                          </DropZone>
                        </CSVUploadSection>
                      </TabContent>
                    );
                  })()}

                {activeTab === 'manual' &&
                  (() => {
                    const {initialX, exitX} = getAnimationDirection(
                      'manual',
                      prevTabRef.current,
                    );
                    return (
                      <TabContent
                        key="manual"
                        initial={{opacity: 0, x: initialX}}
                        animate={{opacity: 1, x: 0}}
                        exit={{opacity: 0, x: exitX}}
                        transition={{duration: 0.25, ease: 'easeInOut'}}>
                        <ManualEntrySection>
                          <TextHeading5 text={t.common.orEnterEmailAddress} />
                          <SectionDescription>
                            <TextHeading7
                              text={
                                hasSSOEnabled
                                  ? 'Add a single employee. Select location, company, and department from your SSO organizational structure.'
                                  : 'Add a single employee with all required fields.'
                              }
                            />
                          </SectionDescription>
                          <ManualInputsGrid>
                            <Input
                              useAlternativeTheme
                              value={manualInvite.fullName}
                              isCompact
                              onChange={handleManualFieldChange('fullName')}
                              placeholder={t.common.fullName}
                            />
                            <Input
                              useAlternativeTheme
                              value={manualInvite.email}
                              isCompact
                              onChange={handleManualFieldChange('email')}
                              placeholder={t.common.email}
                              type="email"
                            />
                            <Input
                              useAlternativeTheme
                              value={manualInvite.jobTitle}
                              isCompact
                              onChange={handleManualFieldChange('jobTitle')}
                              placeholder={t.common.jobTitle}
                            />
                            {hasSSOEnabled ? (
                              <>
                                <StyledSelect
                                  value={manualInvite.ssoOfficeLocation || ''}
                                  onChange={e => {
                                    setManualInvite(prev => ({
                                      ...prev,
                                      ssoOfficeLocation: e.target.value,
                                      ssoCompanyName: '',
                                      ssoDepartmentId: '',
                                      department: '',
                                    }));
                                  }}>
                                  <option value="">Select location...</option>
                                  {locationOptions.map(option => (
                                    <option key={option.id} value={option.id}>
                                      {option.label}
                                    </option>
                                  ))}
                                </StyledSelect>
                                <StyledSelect
                                  value={manualInvite.ssoCompanyName || ''}
                                  disabled={!manualInvite.ssoOfficeLocation}
                                  onChange={e => {
                                    setManualInvite(prev => ({
                                      ...prev,
                                      ssoCompanyName: e.target.value,
                                      ssoDepartmentId: '',
                                      department: '',
                                    }));
                                  }}>
                                  <option value="">Select company...</option>
                                  {subCompanyOptions.map(option => (
                                    <option key={option.id} value={option.id}>
                                      {option.label}
                                    </option>
                                  ))}
                                </StyledSelect>
                                <StyledSelect
                                  value={manualInvite.ssoDepartmentId || ''}
                                  disabled={!manualInvite.ssoCompanyName}
                                  onChange={e => {
                                    const selectedDept = departmentOptions.find(
                                      d => d.id === e.target.value,
                                    );
                                    setManualInvite(prev => ({
                                      ...prev,
                                      ssoDepartmentId: e.target.value,
                                      department: selectedDept?.label || '',
                                    }));
                                  }}>
                                  <option value="">
                                    {t.common.department}...
                                  </option>
                                  {departmentOptions.map(option => (
                                    <option key={option.id} value={option.id}>
                                      {option.label}
                                    </option>
                                  ))}
                                </StyledSelect>
                              </>
                            ) : (
                              <Input
                                useAlternativeTheme
                                value={manualInvite.department}
                                isCompact
                                onChange={handleManualFieldChange('department')}
                                placeholder={t.common.department}
                              />
                            )}
                          </ManualInputsGrid>
                          <ManualActions>
                            <IconButton
                              backgroundColor={Palette.primarySubtleGreen}
                              text={t.common.add}
                              onClick={handleAddManualInvite}
                              icon={<AssetIconAdd />}
                              disabled={!manualFormIsComplete || loading}
                            />
                          </ManualActions>
                        </ManualEntrySection>
                      </TabContent>
                    );
                  })()}

                {activeTab === 'pending' &&
                  (() => {
                    const {initialX, exitX} = getAnimationDirection(
                      'pending',
                      prevTabRef.current,
                    );
                    return (
                      <TabContent
                        key="pending"
                        initial={{opacity: 0, x: initialX}}
                        animate={{opacity: 1, x: 0}}
                        exit={{opacity: 0, x: exitX}}
                        transition={{duration: 0.25, ease: 'easeInOut'}}>
                        <PendingInvitationsSection>
                          <InviteUsersStatus
                            invitedUsers={invitedUsersList}
                            companyId={companyId}
                            onInviteSuccess={fetchInvitedUsers}
                          />
                        </PendingInvitationsSection>
                      </TabContent>
                    );
                  })()}
              </AnimatePresence>

              {error && (
                <div style={{padding: '16px 16px'}}>
                  <TextHeading5 text={error} color={Palette.systemError} />
                </div>
              )}

              {hasEncodingIssues && pendingInvites.length > 0 && (
                <div
                  style={{
                    padding: '12px 16px',
                    margin: '0 16px',
                    background: `${Palette.systemError}15`,
                    border: `1px solid ${Palette.systemError}40`,
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}>
                  <span style={{color: Palette.systemError, fontSize: '16px'}}>
                    !
                  </span>
                  <TextHeading7
                    text="Please fix all names with encoding issues (marked with !) before inviting users."
                    color={Palette.systemError}
                  />
                </div>
              )}

              {pendingInvites.length > 0 &&
                (activeTab === 'csv' || activeTab === 'manual') && (
                  <div ref={tableContainerRef}>
                    <Table>
                      <TableHeader>
                        <tr>
                          <TableHeaderCell>
                            <TextHeading7 text={`${t.common.name}`} />
                          </TableHeaderCell>
                          <TableHeaderCell>
                            <TextHeading7 text={t.common.email} />
                          </TableHeaderCell>
                          <TableHeaderCell>
                            <TextHeading7 text={t.common.department} />
                          </TableHeaderCell>
                          <TableHeaderCell>
                            <TextHeading7 text={t.common.jobTitle} />
                          </TableHeaderCell>
                          <TableHeaderCell style={{width: '80px'}}>
                            <TextHeading7 text={t.common.actions} />
                          </TableHeaderCell>
                        </tr>
                      </TableHeader>
                      <TableBody>
                        {pendingInvites.map(invite => {
                          const nameHasIssue = hasProblematicCharacters(
                            invite.fullName,
                          );
                          return (
                            <tr key={invite.email}>
                              <TableCell>
                                <EditableNameContainer>
                                  <EditableNameInput
                                    type="text"
                                    value={invite.fullName}
                                    onChange={e =>
                                      handleUpdateInviteName(
                                        invite.email,
                                        e.target.value,
                                      )
                                    }
                                    hasWarning={nameHasIssue}
                                    title={
                                      nameHasIssue
                                        ? 'This name contains characters that may have been corrupted during import. Please correct it.'
                                        : ''
                                    }
                                  />
                                  {nameHasIssue && (
                                    <WarningIcon title="Encoding issue detected - please fix the name">
                                      !
                                    </WarningIcon>
                                  )}
                                </EditableNameContainer>
                              </TableCell>
                              <TableCell>
                                <TextHeading8 text={invite.email} />
                              </TableCell>
                              <TableCell>
                                <TextHeading8
                                  text={
                                    invite.department?.label?.[
                                      TranslatedLanguage.English
                                    ] ||
                                    invite.department?.label?.[
                                      TranslatedLanguage.Danish
                                    ] ||
                                    ''
                                  }
                                />
                              </TableCell>
                              <TableCell>
                                <TextHeading8 text={invite.jobTitle} />
                              </TableCell>
                              <TableCell>
                                <IconButton
                                  onClick={() =>
                                    handleDeleteInvite(invite.email)
                                  }
                                  icon={<AssetIconTrash />}
                                />
                              </TableCell>
                            </tr>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
            </ContentArea>

            <ButtonsContainer>
              <SecondaryButton label={t.common.cancel} onClick={onClose} />
              <PrimaryButton
                label={`${t.common.invite} (${pendingInvites.length}) ${t.common.users}`}
                onClick={handleInvite}
                isDisabled={
                  pendingInvites.length === 0 || loading || hasEncodingIssues
                }
              />
            </ButtonsContainer>
          </ModalContent>
        </ModalOverlay>
      </AnimatePresence>
    );
  },
);
